import { isHealthDoc, isMaskFix } from '../../lib/healthMask';
import { MaskFixFirmPanel } from '../health-mask/MaskFixFirmPanel';
// Tablo hücresi için sade firma belge işlemleri: Yükle / Yüklendi + Görüntüle + Sil + statü
import { DependencyRuleNote } from './docDependencies';
import { useEffect, useState } from 'react';
import { UploadHowToDemo } from './UploadHowToDemo';
import { confirmDialog } from '../ConfirmHost';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Copy, Eye, Trash2, Pointer } from 'lucide-react';
import { ApprovedDeleteButton } from './ApprovedDeleteButton';
import { Button } from '../ui/button';
import { DocumentUploadButton } from '../employees/DocumentUploadButton';
import { FirmDocStatusBadge } from './FirmDocFileActions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// "Eksiği Gör": görüntülenene kadar sağdan gelip basan el + parlama animasyonu
// Kullanıcı herhangi bir "Eksiği Gör"e bir kez basınca tüm el animasyonları kalkar (öğrenmiş sayılır)
const TUTORIAL_KEY = 'deficiency-tutorial-seen-v2';
export const DeficiencyButton = ({ onView, testId }) => {
  const [seen, setSeen] = useState(() => localStorage.getItem(TUTORIAL_KEY) === '1');
  useEffect(() => {
    const on = () => setSeen(true);
    window.addEventListener(TUTORIAL_KEY, on);
    return () => window.removeEventListener(TUTORIAL_KEY, on);
  }, []);
  const click = () => { localStorage.setItem(TUTORIAL_KEY, '1'); window.dispatchEvent(new Event(TUTORIAL_KEY)); onView(); };
  return (
    <button type="button" onClick={click} className="relative inline-flex items-center gap-1 rounded bg-rose-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-rose-700" data-testid={testId}>
      {!seen && <span className="press-burst pointer-events-none absolute inset-0 rounded" data-testid={`${testId}-burst`} />}
      Eksiği Gör
      {!seen && <Pointer className="hand-press pointer-events-none absolute -right-4 top-1/2 h-4 w-4 text-slate-800 drop-shadow" data-testid={`${testId}-hand`} />}
    </button>
  );
};

// Eksik/Hatalı talep düzeltme ekranı: onaylıda sadece "Onaylı" + göz, eksikte "Yeni sürüm" + "Eksiği Gör"
const FixModeCell = ({ entityType, entityId, docKey, docLabel, status, note, onUploaded, onDeleted, onView, testPrefix }) => {
  if (status === 'onay') return (
    <div className="flex items-center justify-center gap-1" data-testid={`${testPrefix}-cell`}>
      <FirmDocStatusBadge status="onay" hasFile testId={`${testPrefix}-status`} />
      <button type="button" title="Görüntüle" onClick={onView} className="h-6 w-6 inline-flex items-center justify-center rounded border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid={`${testPrefix}-view`}>
        <Eye className="h-3.5 w-3.5" />
      </button>
      {onDeleted && <ApprovedDeleteButton {...{ entityType, entityId, docKey, docLabel, onDeleted, testPrefix }} />}
    </div>
  );
  return (
    <div className="flex flex-col items-center gap-1" data-testid={`${testPrefix}-cell`}>
      <DocumentUploadButton entityType={entityType} entityId={entityId} docKey={docKey} hasFile frame noGlow label="Yeni sürüm" onUploaded={onUploaded} />
      <DeficiencyButton onView={onView} testId={`${testPrefix}-show-deficiency`} />
    </div>
  );
};

export const FirmDocCell = ({ entityType, entityId, docKey, docLabel, hasFile, status, onUploaded, onDeleted, onView, testPrefix, fixMode, note, optional = false }) => {
  if (fixMode && hasFile && ['onay', 'eksik', 'suresi_doldu'].includes(status)) {
    return <FixModeCell {...{ entityType, entityId, docKey, docLabel, status, note, onUploaded, onDeleted, onView, testPrefix }} />;
  }
  const canDelete = hasFile && ['beklemede', 'eksik', 'suresi_doldu'].includes(status);
  const canReupload = hasFile && status === 'eksik';

  const remove = async () => {
    if (!(await confirmDialog({ title: 'Belge silinsin mi?', description: <>{docLabel} belgesi silinecek. Bu işlem geri alınamaz.<DependencyRuleNote docKey={docKey} /></>, confirmLabel: 'Evet, Sil' }))) return;
    try {
      const res = await axios.delete(`${API}/${entityType}/${entityId}/document-files/${docKey}`);
      onDeleted(res.data);
      toast.success('Belge silindi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge silinemedi');
    }
  };

  const iconBtn = 'h-6 w-6 inline-flex items-center justify-center rounded border';

  return (
    <div className="flex flex-col items-center gap-1 w-full" data-testid={`${testPrefix}-cell`}>
      {!hasFile ? (
        <div className="flex justify-center">
          <DocumentUploadButton entityType={entityType} entityId={entityId} docKey={docKey} hasFile={false} label="Yükle" noGlow optional={optional} onUploaded={onUploaded} />
        </div>
      ) : (
        <div className="flex items-center gap-1">
          <span className="inline-flex items-center gap-0.5 text-[11px] font-medium text-emerald-700" title="Belge yüklendi" data-testid={`${testPrefix}-uploaded`}>
            <CheckCircle className="h-3.5 w-3.5" />Yüklendi
          </span>
          <button type="button" title="Görüntüle" onClick={onView} className={`${iconBtn} border-slate-200 text-slate-700 hover:bg-slate-50`} data-testid={`${testPrefix}-view`}>
            <Eye className="h-3.5 w-3.5" />
          </button>
          {hasFile && status === 'onay' && onDeleted && <ApprovedDeleteButton {...{ entityType, entityId, docKey, docLabel, onDeleted, testPrefix }} />}
          {canDelete && (
            <button type="button" title="Sil" onClick={remove} className={`${iconBtn} border-rose-200 text-rose-600 hover:bg-rose-50`} data-testid={`${testPrefix}-delete`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {canReupload && (
        <div className="flex justify-center">
          <DocumentUploadButton entityType={entityType} entityId={entityId} docKey={docKey} hasFile label="Yeni sürüm" onUploaded={onUploaded} />
        </div>
      )}
      <FirmDocStatusBadge status={status} hasFile={hasFile} testId={`${testPrefix}-status`} hideEmpty onClick={hasFile && ['eksik', 'suresi_doldu'].includes(status) ? onView : undefined}
        label={status === 'eksik' && isHealthDoc(entityType, docKey) && isMaskFix(note) ? 'Maskeleme Düzeltmesi' : undefined} />
    </div>
  );
};

// Belge görüntüleme penceresinde eksiklik maddeleri (kopyalanabilir)
export const DeficiencyList = ({ docLabel, note }) => {
  const items = (note || '').split(/\n|,\s+/).map(t => t.trim()).filter(Boolean);
  const copy = () => navigator.clipboard.writeText(`${docLabel}\n${items.map((t, i) => `${i + 1}. ${t}`).join('\n')}`).then(() => toast.success('Eksiklikler kopyalandı'));
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="viewer-deficiency-list">
      <p className="text-base font-bold text-rose-700 mb-2">Eksiklikler ({items.length})</p>
      {items.length ? (
        <ol className="list-decimal pl-6 space-y-2 text-base font-medium text-slate-900 select-text">
          {items.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}
        </ol>
      ) : <p className="text-base text-slate-500">Eksiklik açıklaması girilmemiş.</p>}
      {items.length > 0 && (
        <Button size="sm" variant="outline" onClick={copy} className="mt-2 h-7 gap-1 text-xs bg-white" data-testid="viewer-deficiency-copy"><Copy className="h-3.5 w-3.5" />Listeyi Kopyala</Button>
      )}
    </div>
  );
};

// Görüntüleyicide eksik belge için "Yeni sürüm yükle" + ilk görüntülemede bir kez mini anlatım
const DEMO_KEY = 'upload-demo-seen-v2';
export const NewVersionUpload = ({ entityType, entityId, docKey, onUploaded, note }) => {
  if (isHealthDoc(entityType, docKey) && isMaskFix(note)) return <MaskFixFirmPanel employeeId={entityId} onUploaded={onUploaded} />;
  return <NewVersionUploadInner entityType={entityType} entityId={entityId} docKey={docKey} onUploaded={onUploaded} />;
};

const NewVersionUploadInner = ({ entityType, entityId, docKey, onUploaded }) => {
  const [seen, setSeen] = useState(() => localStorage.getItem(DEMO_KEY) === '1');
  // Bir kez gösterilince bir daha gösterilmez
  useEffect(() => { localStorage.setItem(DEMO_KEY, '1'); }, []);
  const markSeen = () => setSeen(true);
  return (
    <div className="space-y-3 pt-3" data-testid="viewer-new-version">
      <div className="flex" onClickCapture={markSeen}>
        <DocumentUploadButton entityType={entityType} entityId={entityId} docKey={docKey} hasFile attention label="Yeni sürüm yükle" onUploaded={onUploaded} />
      </div>
      {!seen && <UploadHowToDemo />}
    </div>
  );
};

// Firma, eksik belge görüntüleyici yan paneli: eksiklik listesi + yeni sürüm yükleme
export const FirmDeficiencyExtra = ({ status, docLabel, note, entityType, entityId, docKey, onUploaded }) => (
  ['eksik', 'suresi_doldu'].includes(status) ? (
    <div className="space-y-2">
      <DeficiencyList docLabel={docLabel} note={note} />
      <NewVersionUpload entityType={entityType} entityId={entityId} docKey={docKey} onUploaded={onUploaded} note={note} />
    </div>
  ) : null
);

// Firma, eksik belge tablosu hücresi: kırmızı çerçeveli "Yeni sürüm" + "Eksiği Gör"
export const FirmDeficiencyActions = ({ entityType, entityId, docKey, onUploaded, onView, testPrefix, align = 'items-center' }) => (
  <div className={`flex w-fit flex-col ${align} gap-1`} data-testid={`${testPrefix}-deficiency-actions`}>
    <DocumentUploadButton entityType={entityType} entityId={entityId} docKey={docKey} hasFile frame label="Yeni sürüm" onUploaded={onUploaded} />
    <DeficiencyButton onView={onView} testId={`${testPrefix}-show-deficiency`} />
  </div>
);
